/**
 * Which sales-tax rates apply
 * ---------------------------
 * The one place the rule lives — used by Filing's Tax dropdown (ProcessReceiptsTab)
 * and Masters → Taxes, so both always show the same taxes for an owner:
 *   - only rates in effect ON the date (effective_from <= date <= effective_to);
 *   - an owner with a province / state: that province's rates PLUS the country-wide
 *     ones — except a country-wide GST where the province has HST (HST replaces it);
 *   - an owner with no province: the country-wide rates only;
 *   - no owner chosen yet: every rate in effect.
 *
 * Pure + unit-tested (salesTaxApplicable.test.ts).
 */
type Row = Record<string, any>;

const iso = (v: any) => String(v == null ? '' : v).slice(0, 10);

/** True when the rate is in effect on the date (YYYY-MM-DD). */
export function rateInEffect(rate: Row, onDate: string): boolean {
  const from = iso(rate.effective_from);
  const to = iso(rate.effective_to);
  if (from && from > onDate) return false;   // not yet in effect
  if (to && to < onDate) return false;       // expired before the date
  return true;
}

export interface ApplicableInput {
  rates: Row[];
  types: Row[];
  /** state_id → state code ("SK"). */
  statesById: Map<number, string>;
  /** The owner's province / state code, '' when not known. */
  stateCode: string;
  /** An owner is chosen (with or without a province). */
  hasOwner: boolean;
  /** YYYY-MM-DD; today when empty. */
  onDate?: string;
}

export interface ApplicableResult {
  rates: Row[];
  scopedByProvince: boolean;
  /** The province has HST, so the country-wide GST was left out. */
  ownerIsHST: boolean;
}

export function taxCodeOf(types: Row[], taxTypeId: any): string {
  return types.find(t => String(t.tax_type_id) === String(taxTypeId))?.tax_code || '';
}

/** "PST SK 6%" — code, province code, rate. */
export function salesTaxRateLabel(rate: Row, types: Row[], statesById: Map<number, string>): string {
  const code = taxCodeOf(types, rate.tax_type_id);
  const sc = rate.state_id != null && rate.state_id !== '' ? (statesById.get(Number(rate.state_id)) || '') : '';
  const pct = Number(rate.rate_percent);
  return [code, sc, `${pct}%`].filter(Boolean).join(' ') || rate.label || `${pct}%`;
}

export function applicableSalesTaxRates(input: ApplicableInput): ApplicableResult {
  const { rates, types, statesById, hasOwner } = input;
  const stateCode = String(input.stateCode || '').trim().toUpperCase();
  const onDate = iso(input.onDate) || new Date().toISOString().slice(0, 10);
  const active = rates.filter(r => Number(r.is_deleted) !== 1 && rateInEffect(r, onDate));
  const isCountryWide = (r: Row) => r.state_id == null || r.state_id === '';

  const provinceRates = stateCode
    ? active.filter(r => !isCountryWide(r) && statesById.get(Number(r.state_id)) === stateCode)
    : [];
  const ownerIsHST = provinceRates.some(r => taxCodeOf(types, r.tax_type_id) === 'HST');
  const countryWide = active.filter(r => isCountryWide(r) && !(ownerIsHST && taxCodeOf(types, r.tax_type_id) === 'GST'));

  if (stateCode) return { rates: [...provinceRates, ...countryWide], scopedByProvince: true, ownerIsHST };
  if (hasOwner) return { rates: countryWide, scopedByProvince: false, ownerIsHST: false };
  return { rates: active, scopedByProvince: false, ownerIsHST: false };
}

export interface OwnerTaxLocation {
  /** Province / state code ("SK"), '' when not known. */
  stateCode: string;
  /** Country as stored on the address (name or code), '' when not known. */
  country: string;
  hasOwner: boolean;
  /** A business was somewhere else on the date than it is now (location history). */
  asOfMoved: boolean;
}

/**
 * Where an owner ('business:12', 'account_owner:3' …) was on a date, for its taxes.
 * A business (own or a client's — both live in user_businesses) is resolved AS OF the
 * date from user_business_location_history, falling back to its current address; a
 * person uses their current address.
 */
export function ownerTaxLocation(
  ownerKey: string | null | undefined,
  usersData: Row[],
  businessesData: Row[],
  locationHistory: Row[],
  onDate?: string,
): OwnerTaxLocation {
  if (!ownerKey) return { stateCode: '', country: '', hasOwner: false, asOfMoved: false };
  const [oType, oIdStr] = ownerKey.split(':');
  const oId = Number(oIdStr);
  const date = iso(onDate) || new Date().toISOString().slice(0, 10);
  if (oType === 'business' || oType === 'client_business') {
    const b = businessesData.find(x => Number(x.business_id) === oId);
    const current = String(b?.busi_address_state || '').trim().toUpperCase();
    const country = String(b?.busi_address_country || '').trim();
    const onDateRow = locationHistory.find(h =>
      Number(h.business_id) === oId && Number(h.is_deleted) !== 1 &&
      iso(h.effective_from) <= date &&
      (!h.effective_to || iso(h.effective_to) >= date));
    if (onDateRow && onDateRow.state_code) {
      const sc = String(onDateRow.state_code).trim().toUpperCase();
      return { stateCode: sc, country: String(onDateRow.country || country), hasOwner: !!b, asOfMoved: !!current && sc !== current };
    }
    return { stateCode: current, country, hasOwner: !!b, asOfMoved: false };
  }
  const u = usersData.find(x => Number(x.user_id) === oId);
  return { stateCode: String(u?.address_state || '').trim().toUpperCase(), country: String(u?.address_country || '').trim(), hasOwner: !!u, asOfMoved: false };
}

/** countries_id for a country given by name ("Canada"), iso2 ("CA") or iso3 ("CAN"); null if unknown. */
export function countryIdOf(country: any, countries: Row[]): number | null {
  const c = String(country == null ? '' : country).trim().toLowerCase();
  if (!c) return null;
  const hit = countries.find(r => String(r.name || '').toLowerCase() === c)
    || countries.find(r => c.length === 2 && String(r.iso2 || '').toLowerCase() === c)
    || countries.find(r => c.length === 3 && String(r.iso3 || '').toLowerCase() === c);
  return hit ? Number(hit.countries_id) : null;
}
